/**
 * Text Processing Event Manager
 * Handles text processing event listeners and interactions
 */

import { logger } from '../utils/logger'

export class TextProcessingEventManager {
  private isInitialized: boolean = false

  constructor() {
    logger.debug('TextProcessingEventManager initialized')
  }

  /**
   * Setup text processing event listeners
   */
  public setupTextProcessingEventListeners(): void {
    logger.debug('Setting up text processing event listeners')

    // Auto-resize textareas
    this.setupAutoResizeTextareas()

    // Setup text processing buttons
    this.setupTextProcessingButtons()

    // Setup confidence highlighting
    this.setupConfidenceHighlighting()

    this.isInitialized = true
    logger.debug('Text processing event listeners setup complete')
  }

  /**
   * Setup auto-resize for textareas
   */
  private setupAutoResizeTextareas(): void {
    const textareas = document.querySelectorAll('textarea')
    textareas.forEach(textarea => {
      // Skip if already has auto-resize
      if (textarea.dataset.autoResize === 'true') return

      textarea.dataset.autoResize = 'true'
      
      const autoResize = () => {
        textarea.style.height = 'auto'
        textarea.style.height = textarea.scrollHeight + 'px'
      }

      textarea.addEventListener('input', autoResize)
      textarea.addEventListener('paste', () => {
        setTimeout(autoResize, 10) // Small delay for paste content to be processed
      })

      // Initial resize
      autoResize()
    })
  }

  /**
   * Setup text processing buttons
   */
  private setupTextProcessingButtons(): void {
    // Generate List button
    const generateListBtn = document.getElementById('generate-description-list-btn')
    console.log('🔍 Generate List button found:', !!generateListBtn, generateListBtn)
    if (generateListBtn && !(generateListBtn as HTMLElement).dataset.listenerAttached) {
      ;(generateListBtn as HTMLElement).dataset.listenerAttached = 'true'
      generateListBtn.addEventListener('click', () => {
        console.log('📝 Generate List button clicked!')
        logger.debug('Generate List button clicked')
        // This will be handled by the main App class
        const event = new CustomEvent('generate-list-requested')
        window.dispatchEvent(event)
      })
      console.log('✅ Generate List button listener attached')
    } else if (generateListBtn) {
      console.log('🔍 Generate List button already has listener attached')
    } else {
      console.log('❌ Generate List button not found!')
    }

    // Convert to Coordinates button
    const convertCoordsBtn = document.getElementById('convert-to-coords-btn')
    console.log('🔍 Convert button found:', !!convertCoordsBtn, convertCoordsBtn)
    if (convertCoordsBtn) {
      console.log('🔍 Convert button state:', {
        disabled: (convertCoordsBtn as HTMLButtonElement).disabled,
        className: convertCoordsBtn.className,
        innerHTML: convertCoordsBtn.innerHTML
      })
    }
    
    if (convertCoordsBtn && !(convertCoordsBtn as HTMLElement).dataset.listenerAttached) {
      ;(convertCoordsBtn as HTMLElement).dataset.listenerAttached = 'true'
      convertCoordsBtn.addEventListener('click', () => {
        console.log('🎯 Convert to Coordinates button clicked!')
        logger.debug('Convert to Coordinates button clicked')
        // This will be handled by the main App class
        const event = new CustomEvent('convert-coordinates-requested')
        window.dispatchEvent(event)
      })
      console.log('✅ Convert button listener attached')
    } else if (convertCoordsBtn) {
      console.log('🔍 Convert button already has listener attached')
    } else {
      console.log('❌ Convert button not found!')
    }

  }

  /**
   * Setup confidence highlighting
   */
  private setupConfidenceHighlighting(): void {
    // Setup confidence threshold sliders
    const confidenceSliders = document.querySelectorAll('input[type="range"][data-confidence-threshold]')
    confidenceSliders.forEach(slider => {
      if (!(slider as HTMLElement).dataset.listenerAttached) {
        ;(slider as HTMLElement).dataset.listenerAttached = 'true'
        slider.addEventListener('input', (e) => {
          const threshold = parseInt((e.target as HTMLInputElement).value)
          const selectionId = (e.target as HTMLElement).dataset.selectionId
          
          if (selectionId) {
            logger.debug('Confidence threshold changed:', threshold, 'for selection:', selectionId)
            // This will be handled by the main App class
            const event = new CustomEvent('confidence-threshold-changed', {
              detail: { selectionId, threshold }
            })
            window.dispatchEvent(event)
          }
        })
      }
    })

    // Setup confidence toggle buttons
    const confidenceToggles = document.querySelectorAll('[data-action="toggle-confidence"]')
    confidenceToggles.forEach(btn => {
      if (!(btn as HTMLElement).dataset.listenerAttached) {
        ;(btn as HTMLElement).dataset.listenerAttached = 'true'
        btn.addEventListener('click', (e) => {
          const selectionId = (e.target as HTMLElement).dataset.selectionId
          if (selectionId) {
            logger.debug('Confidence toggle clicked for selection:', selectionId)
            // This will be handled by the main App class
            const event = new CustomEvent('confidence-toggle-requested', {
              detail: { selectionId }
            })
            window.dispatchEvent(event)
          }
        })
      }
    })
  }

  /**
   * Setup text editing events
   */
  public setupTextEditingEvents(): void {
    // Setup text editing buttons
    const editBtns = document.querySelectorAll('[data-action="edit-text"]')
    editBtns.forEach(btn => {
      if (!(btn as HTMLElement).dataset.listenerAttached) {
        ;(btn as HTMLElement).dataset.listenerAttached = 'true'
        btn.addEventListener('click', (e) => {
          const selectionId = (e.target as HTMLElement).dataset.selectionId
          if (selectionId) {
            logger.debug('Edit text button clicked for selection:', selectionId)
            // This will be handled by the main App class
            const event = new CustomEvent('edit-text-requested', {
              detail: { selectionId }
            })
            window.dispatchEvent(event)
          }
        })
      }
    })

    // Setup save buttons
    const saveBtns = document.querySelectorAll('[data-action="save-text"]')
    saveBtns.forEach(btn => {
      if (!(btn as HTMLElement).dataset.listenerAttached) {
        ;(btn as HTMLElement).dataset.listenerAttached = 'true'
        btn.addEventListener('click', (e) => {
          const selectionId = (e.target as HTMLElement).dataset.selectionId
          if (selectionId) {
            logger.debug('Save text button clicked for selection:', selectionId)
            // This will be handled by the main App class
            const event = new CustomEvent('save-text-requested', {
              detail: { selectionId }
            })
            window.dispatchEvent(event)
          }
        })
      }
    })
  }

  /**
   * Setup modal events
   */
  public setupModalEvents(): void {
    // Setup modal close buttons
    const modalCloseBtns = document.querySelectorAll('[data-action="close-modal"]')
    modalCloseBtns.forEach(btn => {
      if (!(btn as HTMLElement).dataset.listenerAttached) {
        ;(btn as HTMLElement).dataset.listenerAttached = 'true'
        btn.addEventListener('click', () => {
          logger.debug('Modal close button clicked')
          // This will be handled by the main App class
          const event = new CustomEvent('modal-close-requested')
          window.dispatchEvent(event)
        })
      }
    })

    // Setup modal file upload
    const fileInputs = document.querySelectorAll('input[type="file"][data-modal-upload]')
    fileInputs.forEach(input => {
      if (!(input as HTMLElement).dataset.listenerAttached) {
        ;(input as HTMLElement).dataset.listenerAttached = 'true'
        input.addEventListener('change', (e) => {
          const file = (e.target as HTMLInputElement).files?.[0]
          if (file) {
            logger.debug('Modal file upload:', file.name)
            // This will be handled by the main App class
            const event = new CustomEvent('modal-file-upload-requested', {
              detail: { file }
            })
            window.dispatchEvent(event)
          }
        })
      }
    })
  }

  /**
   * Reset initialization state
   */
  public reset(): void {
    this.isInitialized = false
    logger.debug('TextProcessingEventManager reset')
  }

  /**
   * Check if initialized
   */
  public isInitializedState(): boolean {
    return this.isInitialized
  }

  /**
   * Cleanup event listeners
   */
  public cleanup(): void {
    // Remove all event listeners by removing the dataset flags
    const elementsWithListeners = document.querySelectorAll('[data-listener-attached="true"]')
    elementsWithListeners.forEach(element => {
      element.removeAttribute('data-listener-attached')
    })

    this.isInitialized = false
    logger.debug('TextProcessingEventManager cleaned up')
  }
}
